import React from 'react';
import { useContent } from '../../hooks/useContent';
import { uploadsAbsoluteUrl } from '../../utils/uploadsBase';
import styles from './AboutJournal.module.css';

const CARD_IDS = ['card1', 'card2', 'card3', 'card4', 'card5'];
const BULLET_KEYS = ['bullet1', 'bullet2', 'bullet3', 'bullet4', 'bullet5'];
const BENEFIT_IDS = ['b1', 'b2', 'b3', 'b4'];

const TopicIcon = ({ variant }) => {
  if (variant === 'energy') {
    return (
      <svg className={styles.topicIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (variant === 'circles') {
    return (
      <svg className={styles.topicIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    );
  }
  if (variant === 'waves') {
    return (
      <svg className={styles.topicIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="2" />
        <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
      </svg>
    );
  }
  if (variant === 'wifi') {
    return (
      <svg className={styles.topicIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12.55a11 11 0 0 1 14.08 0" />
        <path d="M1.42 9a16 16 0 0 1 21.16 0" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
    );
  }
  return (
    <svg className={styles.topicIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3" />
    </svg>
  );
};

const TOPIC_ICONS = ['energy', 'circles', 'waves', 'wifi', 'cross'];

const BENEFIT_FALLBACK = {
  b1: {
    title: 'Global Visibility',
    text: 'Your research will reach thousands of readers worldwide.',
  },
  b2: {
    title: 'Prestigious Indexing',
    text: 'Our journal is indexed in top scientific databases.',
  },
  b3: {
    title: 'Expert Peer Review',
    text: 'Your work will be evaluated by leading specialists in the field.',
  },
  b4: {
    title: 'Fast Publication Process',
    text: 'We ensure an efficient and timely publication process.',
  },
};

const TOPIC_SECTION_FALLBACK = {
  sectionTitle: 'Shape the Future of Science with us!',
  subtitle:
    'The scope of the journal covers a wide range of topics, including but not limited to:',
};

const TOPIC_CARD_DEFAULTS = {
  card1: {
    titleLine1: 'Energy sustainable',
    titleLine2: 'development',
    bullet1: 'Renewable Energy Sources and Power Supply Systems',
    bullet2: 'Environmental Engineering and Sustainable Development',
    bullet3: 'Electric Drives',
    bullet4: 'Ecology and Energy',
    bullet5: '',
  },
  card2: {
    titleLine1: 'Materials, Mechanics',
    titleLine2: 'and Manufacturing',
    bullet1:
      'Materials Science and Applied Mechanics Industrial and Manufacturing Engineering Chemical and Process Engineering',
    bullet2: 'Nanotechnology and Applied Physics Mechanical Systems and Robotics',
    bullet3: '',
    bullet4: '',
    bullet5: '',
  },
  card3: {
    titleLine1: 'Transportation and logistics',
    titleLine2: 'systems',
    bullet1: 'Automotive and Aerospace Engineering',
    bullet2:
      'Innovations in Civil and Structural Design Engineering and Industrial Systems Management Engineering Economics',
    bullet3: '',
    bullet4: '',
    bullet5: '',
  },
  card4: {
    titleLine1: 'Digital technologies and',
    titleLine2: 'Telecommunications',
    bullet1: 'Artificial Intelligence in Engineering Internet of Things (IoT) and Embedded Systems',
    bullet2: 'Computer Engineering and Modeling',
    bullet3: 'Information and Telecommunication Technologies',
    bullet4: 'Wireless and Mobile Technologies',
    bullet5: 'Communication Protocols and Network Management',
  },
  card5: {
    titleLine1: 'Engineering workforce',
    titleLine2: 'development',
    bullet1: 'Digitalization in Science and Education Project Management',
    bullet2: 'Problem-Solving and Analysis',
    bullet3: '',
    bullet4: '',
    bullet5: '',
  },
};

const AboutJournal = () => {
  const { t } = useContent();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const img = (key, fallback) => {
    const path = t(key, fallback);
    if (!path) return fallback;
    const s = String(path);
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/uploads')) return uploadsAbsoluteUrl(s, API_URL);
    return s || fallback;
  };

  const topicBullets = (cardId) => {
    const defs = TOPIC_CARD_DEFAULTS[cardId] || {};
    return BULLET_KEYS.map((bk) => t(`aboutJournal.topics.${cardId}.${bk}`, defs[bk] ?? '')).filter((x) =>
      String(x).trim(),
    );
  };

  return (
    <div className={styles.container}>
      <section className={styles.heroSection}>
        <h1 className={styles.heroTitle}>{t('aboutJournal.hero.title', 'JOURNAL')}</h1>
      </section>

      <section className={styles.mainContent}>
        <div className={styles.contentGrid}>
          <div className={styles.leftContent}>
            <h2 className={styles.mainTitle}>
              {t('aboutJournal.main.title', 'The Advances in Applied Engineering Sciences')}
            </h2>
            <p className={styles.description}>
              {t(
                'aboutJournal.main.description',
                'A peer-reviewed journal dedicated to the latest advancements and practical innovations in applied engineering sciences. The goal of this publication is to present research that offers cutting-edge solutions to pressing engineering and technological challenges, supporting both theoretical progress and the practical application of engineering solutions. Key areas of focus include sustainable energy systems, renewable energy sources, advanced materials, digital technologies, mechanical and transportation systems, and the development of human capital in engineering. The journal caters to a broad readership, including researchers, practitioners, and industry professionals. Publications emphasize real-world applications of innovations that enhance productivity and promote the sustainable development of industrial processes. "Advances in Applied Engineering Sciences" is committed to maintaining high standards of peer review and to publish impactful research that helps shape the future of applied engineering.',
              )}
            </p>
          </div>
          <div className={styles.rightContent}>
            <img
              src={img('aboutJournal.main.image', '/Images/journal.jpg')}
              alt={t('aboutJournal.main.imageAlt', 'Research Team')}
              className={styles.contentImage}
            />
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>
            {t(
              'aboutJournal.cta.title',
              'Publish your Research in our Esteemed Scientific Journal!',
            )}
          </h2>
          <p className={styles.ctaText}>
            {t(
              'aboutJournal.cta.text',
              'Our journal serves as a platform for publishing high-quality scientific research and innovations. We are committed to encouraging studies that address current issues in scientific and technological advancement.',
            )}
          </p>
        </div>
      </section>

      <section className={styles.benefitsSection}>
        <div className={styles.benefitsGrid}>
          {BENEFIT_IDS.map((id) => (
            <div key={id} className={styles.benefitCard}>
              <h3 className={styles.benefitTitle}>
                {t(`aboutJournal.benefits.${id}.title`, BENEFIT_FALLBACK[id].title)}
              </h3>
              <p className={styles.benefitText}>
                {t(`aboutJournal.benefits.${id}.text`, BENEFIT_FALLBACK[id].text)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.topicsSection}>
        <h2 className={styles.sectionTitle}>
          {t('aboutJournal.topics.sectionTitle', TOPIC_SECTION_FALLBACK.sectionTitle)}
        </h2>
        <p className={styles.topicsSubtitle}>
          {t('aboutJournal.topics.subtitle', TOPIC_SECTION_FALLBACK.subtitle)}
        </p>

        <div className={styles.topicsGrid}>
          {CARD_IDS.map((cardId, idx) => {
            const bullets = topicBullets(cardId);
            const d = TOPIC_CARD_DEFAULTS[cardId] || {};
            const t1 = t(`aboutJournal.topics.${cardId}.titleLine1`, d.titleLine1 ?? '');
            const t2 = t(`aboutJournal.topics.${cardId}.titleLine2`, d.titleLine2 ?? '');
            return (
              <div key={cardId} className={styles.topicCard}>
                <div className={styles.topicHeader}>
                  <TopicIcon variant={TOPIC_ICONS[idx]} />
                  <div>
                    {t1 ? <h3 className={styles.topicTitle}>{t1}</h3> : null}
                    {t2 ? <h3 className={styles.topicTitle}>{t2}</h3> : null}
                  </div>
                </div>
                {bullets.length > 0 ? (
                  <ul className={styles.topicList}>
                    {bullets.map((line, i) => (
                      <li key={`${cardId}-${i}`}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.joinSection}>
        <h2 className={styles.joinTitle}>{t('aboutJournal.join.title', 'JOIN OUR SCIENTIFIC COMMUNITY TODAY!')}</h2>

        <div className={styles.infoList}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.frequencyLabel', 'Publishing frequency:')}</span>
            <span className={styles.infoValue}>
              {t('aboutJournal.join.frequencyValue', 'twice a year - starting from 2025.')}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.yearLabel', 'Year of the first number:')}</span>
            <span className={styles.infoValue}>{t('aboutJournal.join.yearValue', '2026')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.issnLabel', 'ISSN:')}</span>
            <span className={styles.infoValue}>{t('aboutJournal.join.issnValue', 'need to order (on-line)')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.publisherLabel', 'Publisher:')}</span>
            <span className={styles.infoValue}>
              {t(
                'aboutJournal.join.publisherValue',
                'Tashkent University or Anhalt University of Applied Sciences',
              )}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.languageLabel', 'Language:')}</span>
            <span className={styles.infoValue}>{t('aboutJournal.join.languageValue', 'English')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.reviewLabel', 'Review type:')}</span>
            <span className={styles.infoValue}>{t('aboutJournal.join.reviewValue', 'double-blind peer review')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.licenceLabel', 'Licence:')}</span>
            <span className={styles.infoValue}>
              {t(
                'aboutJournal.join.licenceValue',
                'Creative Commons Attribution-ShareAlike 4.0 International License (CC BY-SA 4.0)',
              )}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('aboutJournal.join.archiveLabel', 'Self-archiving policy:')}</span>
            <span className={styles.infoValue}>
              {t(
                'aboutJournal.join.archiveValue',
                "the author is allowed to publish the postprints and publisher's version of the articles",
              )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutJournal;
